"use client";

import { CircleHelpIcon, MoreHorizontalIcon } from "lucide-react";
import type * as React from "react";

import { cn } from "../cn";
import { Button } from "./button";
import { CountPill } from "./count-pill";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";

// Page templates, header half (design spec §2 Main, §6.1, §8.2): the page frame, DTP's eyebrow
// over a 28 px title, the section heading and the 28 px help trigger.

const FRAME_WIDTH = {
  /** Document pages: padding 28 32 48 (20 16 40 below 768), centred at 1440. */
  doc: "mx-auto w-full max-w-page px-8 pt-7 pb-12 max-md:px-4 max-md:pt-5 max-md:pb-10",
  /** Prose pages: the same padding, 760 wide. */
  prose:
    "mx-auto w-full max-w-(--prose-max-w) px-8 pt-7 pb-12 max-md:px-4 max-md:pt-5 max-md:pb-10",
  /** Table and board bodies: edge to edge, no padding. */
  full: "w-full",
} as const;

export type PageFrameProps = React.ComponentProps<"div"> & { width?: keyof typeof FRAME_WIDTH };

export function PageFrame({ width = "doc", className, ...props }: PageFrameProps) {
  return (
    <div
      data-slot="page-frame"
      data-width={width}
      className={cn(FRAME_WIDTH[width], className)}
      {...props}
    />
  );
}

/** 10 px / 700 / 1.5 px tracking, uppercase, in --eyebrow (lighter cobalt in dark). */
export function Eyebrow({ className, ...props }: React.ComponentProps<"p">) {
  return (
    <p
      data-slot="eyebrow"
      className={cn(
        "mb-2 text-[10px] leading-4 font-bold tracking-eyebrow text-eyebrow uppercase",
        className,
      )}
      {...props}
    />
  );
}

export type InfoHelpProps = {
  children: React.ReactNode;
  /** Accessible name of the trigger. */
  label?: string;
  className?: string;
};

/**
 * 28 px ghost help button; the explanation opens on hover, focus or click (a popover, so it
 * works on touch too), at most 310 px wide in 13 / 1.55.
 */
export function InfoHelp({ children, label = "More about this", className }: InfoHelpProps) {
  return (
    <Popover>
      <PopoverTrigger
        openOnHover
        delay={150}
        render={
          <Button
            variant="ghost"
            size="icon-sm"
            aria-label={label}
            data-slot="info-help"
            className={cn("text-muted-foreground", className)}
          />
        }
      >
        <CircleHelpIcon />
      </PopoverTrigger>
      <PopoverContent
        side="bottom"
        align="start"
        className="w-auto max-w-[310px] p-3 text-[13px] leading-[1.55] text-secondary"
      >
        {children}
      </PopoverContent>
    </Popover>
  );
}

export type PageHeaderAction = { label: string; onSelect?: () => void; href?: string };

export type PageHeaderProps = Omit<React.ComponentProps<"header">, "title"> & {
  eyebrow?: React.ReactNode;
  title: React.ReactNode;
  /** Inline after the title: kind or state `Badge`. */
  badge?: React.ReactNode;
  subtitle?: React.ReactNode;
  /** The breadcrumb row above the eyebrow (pass `Breadcrumb` / the shell's `Breadcrumbs`). */
  breadcrumbs?: React.ReactNode;
  /** Explanation behind the 28 px help button after the title. */
  help?: React.ReactNode;
  /** Right-hand buttons: the primary `Button size="lg"` first, then secondary / ghost. */
  actions?: React.ReactNode;
  /**
   * The same actions as menu items: below 640 px the buttons collapse into a "…" menu built
   * from these. Without them the buttons wrap under the title on phones.
   */
  menuActions?: PageHeaderAction[];
  /** `tabs`: view tabs follow, so the bottom margin is 20 instead of 28. */
  spacing?: "default" | "tabs";
};

export function PageHeader({
  eyebrow,
  title,
  badge,
  subtitle,
  breadcrumbs,
  help,
  actions,
  menuActions,
  spacing = "default",
  className,
  ...props
}: PageHeaderProps) {
  const collapses = Boolean(menuActions?.length);
  return (
    <header
      data-slot="page-header"
      className={cn(
        "flex items-start justify-between gap-6",
        spacing === "tabs" ? "mb-5" : "mb-7",
        !collapses && "max-sm:flex-wrap",
        className,
      )}
      {...props}
    >
      <div className="min-w-0 max-w-(--prose-max-w)">
        {breadcrumbs ? (
          <div data-slot="page-header-breadcrumbs" className="mb-2">
            {breadcrumbs}
          </div>
        ) : null}
        {eyebrow ? <Eyebrow>{eyebrow}</Eyebrow> : null}
        <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
          <h1 className="min-w-0 font-heading text-2xl font-strong tracking-title text-foreground max-md:text-xl max-md:tracking-[-0.6px]">
            {title}
          </h1>
          {badge}
          {help ? <InfoHelp>{help}</InfoHelp> : null}
        </div>
        {subtitle ? (
          <p className="mt-1.5 text-sm leading-[1.65] text-muted-foreground">{subtitle}</p>
        ) : null}
      </div>
      {actions ? (
        <div
          data-slot="page-header-actions"
          className={cn("flex shrink-0 items-center gap-2", collapses && "max-sm:hidden")}
        >
          {actions}
        </div>
      ) : null}
      {collapses ? (
        <div className="shrink-0 sm:hidden">
          <DropdownMenu>
            <DropdownMenuTrigger
              render={<Button variant="secondary" size="icon" aria-label="Page actions" />}
            >
              <MoreHorizontalIcon />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-44">
              {menuActions?.map((a) => (
                <DropdownMenuItem
                  key={a.label}
                  onClick={a.onSelect}
                  // biome-ignore lint/a11y/useAnchorContent: Base UI renders the item's children (the label) into this anchor.
                  render={a.href ? <a href={a.href} /> : undefined}
                >
                  {a.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ) : null}
    </header>
  );
}

export type SectionHeadingProps = Omit<React.ComponentProps<"div">, "title"> & {
  title: React.ReactNode;
  count?: number;
  description?: React.ReactNode;
  actions?: React.ReactNode;
  level?: 2 | 3;
  /** Id on the heading, for `section aria-labelledby`. */
  headingId?: string;
};

/** h2 17 / 620 / −0.3 (h3 15 / 600) with an optional count pill; 16 px below. */
export function SectionHeading({
  title,
  count,
  description,
  actions,
  level = 2,
  headingId,
  className,
  ...props
}: SectionHeadingProps) {
  const Heading = level === 2 ? "h2" : "h3";
  return (
    <div
      data-slot="section-heading"
      className={cn("mb-4 flex items-start justify-between gap-4", className)}
      {...props}
    >
      <div className="min-w-0">
        <div className="flex items-center gap-2">
          <Heading
            id={headingId}
            className={cn(
              "font-heading text-foreground",
              level === 2
                ? "text-[17px] leading-6 font-title tracking-[-0.3px]"
                : "text-[15px] leading-[22px] font-semibold",
            )}
          >
            {title}
          </Heading>
          {count !== undefined ? <CountPill count={count} /> : null}
        </div>
        {description ? (
          <p className="mt-1 text-sm leading-[1.65] text-muted-foreground">{description}</p>
        ) : null}
      </div>
      {actions ? <div className="flex shrink-0 items-center gap-2">{actions}</div> : null}
    </div>
  );
}
